import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { ResumeDto } from "@reactive-resume/dto";
import { CustomSectionGroup, defaultSection, SectionKey } from "@reactive-resume/schema";
import { removeItemInLayout } from "@reactive-resume/utils";
import _set from "lodash.set";
import get from "lodash.get";
import { temporal, TemporalState } from "zundo";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useStoreWithEqualityFn } from "zustand/traditional";

import { debouncedUpdateResume } from "../services/resume";

type ResumeStore = {
  resume: ResumeDto;

  // Actions
  setValue: (path: string, value: unknown) => void;

  // Custom Section Actions
  addSection: () => void;
  removeSection: (sectionId: SectionKey) => void;
  duplicateSection: (sectionId: SectionKey) => void;
};

export const useResumeStore = create<ResumeStore>()(
  temporal(
    immer((set) => ({
      resume: {} as ResumeDto,
      setValue: (path, value) => {
        set((state) => {
          // Freeze typography font settings (size, family, subset, variants) and theme colors only for mastersunion template
          const currentTemplate = state.resume.data?.metadata?.template;
          if (
            currentTemplate === "mastersunion" &&
            (path.startsWith("metadata.typography.font.") ||
              path.startsWith("metadata.theme."))
          ) {
            return;
          }

          if (path === "visibility") {
            state.resume.visibility = value as "public" | "private";
          } else {
            // Mutate draft in place; reassigning `data` after lodash.set can break Immer proxies
            // and drop nested keys (e.g. custom sections vanish from preview after edits).
            _set(state.resume.data, path, value);
          }

          debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
        });
      },
      addSection: () => {
        const section: CustomSectionGroup = {
          ...defaultSection,
          id: createId(),
          name: t`Custom Section`,
          items: [],
        };

        set((state) => {
          const lastPageIndex = state.resume.data.metadata.layout.length - 1;
          state.resume.data.metadata.layout[lastPageIndex][0].push(`custom.${section.id}`);
          _set(state.resume.data, `sections.custom.${section.id}`, section);

          debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
        });
      },
      removeSection: (sectionId: SectionKey) => {
        if (sectionId.startsWith("custom.")) {
          const id = sectionId.split("custom.")[1];

          set((state) => {
            removeItemInLayout(sectionId, state.resume.data.metadata.layout);
            delete state.resume.data.sections.custom[id];

            debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
          });
        }
      },
      duplicateSection: (sectionId: SectionKey) => {
        set((state) => {
          const sourceSection = get(state.resume.data.sections, sectionId);
          if (!sourceSection) return;

          const newId = createId();
          let duplicatedSection: CustomSectionGroup;

          // Helper function to map different section types to custom section format
          const mapItemToCustom = (item: any, sectionType: string) => {
            const baseItem = {
              id: createId(),
              visible: item.visible ?? true,
              keywords: item.keywords || [],
              url: item.url || { label: "", href: "" },
              company: item.company || { label: "", href: "" },
              companyDescription: item.companyDescription || "",
              designations: item.designations || [],
            };

            switch (sectionType) {
              case "custom":
                return {
                  ...baseItem,
                  name: item.name || "",
                  description: item.description || "",
                  date: item.date || "",
                  location: item.location || "",
                  summary: item.summary || "",
                  company: item.company || { label: "", href: "" },
                  companyDescription: item.companyDescription || "",
                  designations: item.designations || [],
                };
              case "skills":
                return {
                  ...baseItem,
                  name: item.name || "",
                  description: item.description || "",
                  date: "", // Skills don't have dates
                  location: "", // Skills don't have locations
                  summary: item.description || "", // Use description as summary
                };
              case "experience":
                return {
                  ...baseItem,
                  name: (typeof item.company === 'object' && item.company !== null
                    ? (item.company.label || item.company.href || "")
                    : (item.company || "")),
                  description: item.position || "",
                  date: item.date || "",
                  location: item.location || "",
                  summary: item.summary || "",
                };
              case "internship":
                return {
                  ...baseItem,
                  name: (typeof item.company === 'object' && item.company !== null
                    ? (item.company.label || item.company.href || "")
                    : (item.company || "")),
                  description: item.position || "",
                  date: item.date || "",
                  location: item.location || "",
                  summary: item.summary || "",
                };
              case "certifications":
                return {
                  ...baseItem,
                  name: item.name || "",
                  description: item.issuer || "",
                  date: item.date || "",
                  location: "", // Certifications don't have locations
                  summary: item.summary || "",
                };
              case "education":
                return {
                  ...baseItem,
                  name: (typeof item.institution === 'object' && item.institution !== null
                    ? (item.institution.label || item.institution.href || "")
                    : (item.institution || "")),
                  description: item.area || "",
                  date: item.date || "",
                  location: item.location || "",
                  summary: item.summary || "",
                };
              case "projects":
                return {
                  ...baseItem,
                  name: item.name || "",
                  description: item.description || "",
                  date: item.date || "",
                  location: "", // Projects don't have locations
                  summary: item.summary || "",
                };
              case "awards":
                return {
                  ...baseItem,
                  name: item.title || "",
                  description: item.awarder || "",
                  date: item.date || "",
                  location: "", // Awards don't have locations
                  summary: item.summary || "",
                };
              case "publications":
                return {
                  ...baseItem,
                  name: item.name || "",
                  description: item.publisher || "",
                  date: item.date || "",
                  location: "", // Publications don't have locations
                  summary: item.summary || "",
                };
              case "volunteer":
                return {
                  ...baseItem,
                  name: item.organization || "",
                  description: item.position || "",
                  date: item.date || "",
                  location: item.location || "",
                  summary: item.summary || "",
                };
              case "extracurriculars":
                return {
                  ...baseItem,
                  name: item.organization || "",
                  description: item.position || "",
                  date: item.date || "",
                  location: item.location || "",
                  summary: item.summary || "",
                };
              case "interests":
                return {
                  ...baseItem,
                  name: item.name || "",
                  description: "", // Interests don't have descriptions
                  date: "", // Interests don't have dates
                  location: "", // Interests don't have locations
                  summary: "", // Interests don't have summaries
                };
              case "languages":
                return {
                  ...baseItem,
                  name: item.name || "",
                  description: item.description || "",
                  date: "", // Languages don't have dates
                  location: "", // Languages don't have locations
                  summary: "", // Languages don't have summaries
                };
              case "profiles":
                return {
                  ...baseItem,
                  name: item.network || "",
                  description: item.username || "",
                  date: "", // Profiles don't have dates
                  location: "", // Profiles don't have locations
                  summary: "", // Profiles don't have summaries
                };
              case "references":
                return {
                  ...baseItem,
                  name: item.name || "",
                  description: item.description || "",
                  date: "", // References don't have dates
                  location: "", // References don't have locations
                  summary: "", // References don't have summaries
                };
              default:
                // For custom sections, preserve the original structure
                return {
                  ...baseItem,
                  name: item.name || "",
                  description: item.description || "",
                  date: item.date || "",
                  location: item.location || "",
                  summary: item.summary || "",
                };
            }
          };

          if (sectionId.startsWith("custom.")) {
            // Duplicate custom section
            const customSection = sourceSection as CustomSectionGroup;
            duplicatedSection = {
              ...customSection,
              id: newId,
              name: `${customSection.name} (Copy)`,
              items: customSection.items.map(item => mapItemToCustom(item, "custom")),
            };
          } else {
            // Duplicate built-in section as custom section
            const builtInSection = sourceSection as any;
            const sectionType = sectionId; // The sectionId is the type (e.g., "skills", "experience")
            duplicatedSection = {
              ...defaultSection,
              id: newId,
              name: `${builtInSection.name} (Copy)`,
              items: builtInSection.items ? builtInSection.items.map((item: any) => mapItemToCustom(item, sectionType)) : [],
            };
          }

          const lastPageIndex = state.resume.data.metadata.layout.length - 1;
          state.resume.data.metadata.layout[lastPageIndex][0].push(`custom.${newId}`);
          _set(state.resume.data, `sections.custom.${newId}`, duplicatedSection);

          debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
        });
      },
    })),
    {
      limit: 100,
      wrapTemporal: (fn) => devtools(fn),
      partialize: ({ resume }) => ({ resume }),
    },
  ),
);

export const useTemporalResumeStore = <T>(
  selector: (state: TemporalState<Pick<ResumeStore, "resume">>) => T,
  equality?: (a: T, b: T) => boolean,
) => useStoreWithEqualityFn(useResumeStore.temporal, selector, equality);
